import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCheckbox, SpecraSegmentedControl, SpecraSelect, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageBody, PageHero } from '../../components/layout';
import { useGrowthThreadEnabled } from '../GrowthHub/growthFlags';
import { useCanonicalOpportunities } from '../Opportunities/hooks/useCanonicalOpportunities';
import { AccessNote } from './components/AccessNote';
import { LandscapeEconomicsPanel } from './components/LandscapeEconomicsPanel';
import { LandscapeHistory } from './components/LandscapeHistory';
import { LandscapeMap } from './components/LandscapeMap';
import { LandscapeStory } from './components/LandscapeStory';
import { LandscapeStrategy } from './components/LandscapeStrategy';
import { LandscapeSynthesisPanel } from './components/LandscapeSynthesisPanel';
import { LandscapeWhitespace } from './components/LandscapeWhitespace';
import { MemberRow } from './components/MemberRow';
import { ProgramReferences } from './components/ProgramReferences';
import { useLandscapeComparison, useLandscapeIntelligence, useLandscapeWriteAccess } from './hooks/useLandscapeStrategy';
import { useLandscape, useLandscapeMemberActions, useLandscapeMembers } from './hooks/useLandscapes';
import { useLandscapeView } from './hooks/useLandscapeView';
import { criterionLabel } from './landscapeForm';
export function LandscapeDetailPage() {
    const { id = '' } = useParams();
    const comparison = useLandscapeComparison(id);
    const intelligence = useLandscapeIntelligence(id);
    if (!comparison.data || comparison.error || !intelligence.data || intelligence.error) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx(LandscapeStrategy, {
                landscapeId: id
            })
        });
    }
    return /*#__PURE__*/ _jsx(GrowthLandscapeDetail, {}, id);
}
function GrowthLandscapeDetail() {
    const { id = '' } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const threadOn = useGrowthThreadEnabled();
    // The list hands its filter query along so "Back" returns to the same filtered view.
    const listSearch = location.state?.listSearch ?? '';
    const [params, setParams] = useSearchParams();
    const showRemoved = params.get('removed') === '1';
    const setShowRemoved = (show)=>setParams((prev)=>{
            const next = new URLSearchParams(prev);
            if (show) next.set('removed', '1');
            else next.delete('removed');
            return next;
        }, {
            replace: true,
            state: location.state
        });
    const [view, setView] = useLandscapeView();
    const [picked, setPicked] = useState('');
    const [lastRun, setLastRun] = useState(null);
    const canWrite = useLandscapeWriteAccess().allowed;
    const landscape = useLandscape(id);
    const members = useLandscapeMembers(id, showRemoved);
    const opportunities = useCanonicalOpportunities();
    const { add, remove, runRules } = useLandscapeMemberActions(id);
    const rows = members.data ?? [];
    const activeIds = new Set(rows.filter((m)=>m.state === 'active').map((m)=>m.opportunityId));
    const candidates = (opportunities.data ?? []).filter((o)=>!activeIds.has(o.id));
    const l = landscape.data;
    const fail = (what)=>()=>toast.error(`Could not ${what}`);
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.landscapes.detailPage,
        "data-landscape-id": id,
        children: /*#__PURE__*/ _jsxs(PageBody, {
            children: [
                /*#__PURE__*/ _jsx(PageHero, {
                    title: l?.name ?? 'Landscape',
                    subtitle: l?.scope,
                    actions: /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            threadOn && /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                                label: "Page",
                                value: "overview",
                                options: [
                                    {
                                        value: 'overview',
                                        label: 'Overview'
                                    },
                                    {
                                        value: 'seats',
                                        label: 'Seat history'
                                    }
                                ],
                                onValueChange: (v)=>{
                                    if (v === 'seats') navigate(`/landscapes/${id}/seats`);
                                },
                                testId: Testids.landscapeSeats.viewToggle
                            }),
                            /*#__PURE__*/ _jsx(Link, {
                                to: `/landscapes${listSearch}`,
                                className: specraButtonVariants({
                                    variant: 'ghost',
                                    size: 'sm'
                                }),
                                children: "All Landscapes"
                            }),
                            /*#__PURE__*/ _jsx(Link, {
                                to: `/landscapes/${id}/briefs`,
                                className: specraButtonVariants({
                                    variant: 'outline',
                                    size: 'sm'
                                }),
                                "data-testid": Testids.landscapeReports.detailLink,
                                children: "Review briefs"
                            }),
                            canWrite && /*#__PURE__*/ _jsx(Link, {
                                to: `/landscapes/${id}/edit`,
                                className: specraButtonVariants({
                                    variant: 'outline',
                                    size: 'sm'
                                }),
                                children: "Edit"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-6",
                    children: [
                        /*#__PURE__*/ _jsx(AccessNote, {}),
                        landscape.error && /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "Could not load this Landscape."
                        }),
                        l && /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-wrap items-center gap-2 text-sm",
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    "data-testid": Testids.landscapes.detailActiveCount,
                                    children: [
                                        l.activeMemberCount,
                                        " active"
                                    ]
                                }),
                                l.observedOnly && /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "outline",
                                    children: "Observed only"
                                }),
                                l.criteria.map((c)=>/*#__PURE__*/ _jsxs(SpecraBadge, {
                                        variant: "neutral",
                                        children: [
                                            criterionLabel(c.kind),
                                            " = ",
                                            c.value
                                        ]
                                    }, `${c.kind}:${c.value}`))
                            ]
                        }),
                        /*#__PURE__*/ _jsx(LandscapeEconomicsPanel, {
                            landscapeId: id
                        }),
                        /*#__PURE__*/ _jsx(LandscapeStrategy, {
                            landscapeId: id
                        }),
                        /*#__PURE__*/ _jsx(LandscapeSynthesisPanel, {
                            landscapeId: id,
                            landscapeName: l?.name ?? ''
                        }),
                        canWrite && /*#__PURE__*/ _jsxs("section", {
                            className: "flex flex-wrap items-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    disabled: runRules.isPending,
                                    "data-testid": Testids.landscapes.runRulesButton,
                                    onClick: ()=>{
                                        setLastRun(null);
                                        runRules.mutate(undefined, {
                                            onSuccess: (r)=>setLastRun({
                                                    added: r.added,
                                                    resuggested: r.resuggested,
                                                    suppressed: r.suppressed,
                                                    unchanged: r.unchanged
                                                }),
                                            onError: fail('run rules')
                                        });
                                    },
                                    children: "Run rules"
                                }),
                                lastRun && /*#__PURE__*/ _jsxs("span", {
                                    className: "text-sm",
                                    "data-testid": Testids.landscapes.runRulesResult,
                                    "data-added": lastRun.added,
                                    "data-resuggested": lastRun.resuggested,
                                    "data-suppressed": lastRun.suppressed,
                                    "data-unchanged": lastRun.unchanged,
                                    children: [
                                        lastRun.added,
                                        " added · ",
                                        lastRun.resuggested,
                                        " re-suggested · ",
                                        lastRun.suppressed,
                                        ' ',
                                        "suppressed · ",
                                        lastRun.unchanged,
                                        " unchanged"
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center justify-between gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx("h2", {
                                            className: "text-base font-semibold",
                                            children: "Members"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-2 text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsxs("fieldset", {
                                                    className: "flex items-center gap-1",
                                                    "aria-label": "Members view",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                                            size: "sm",
                                                            variant: view === 'list' ? 'default' : 'outline',
                                                            "aria-pressed": view === 'list',
                                                            "data-testid": Testids.landscapes.viewListButton,
                                                            onClick: ()=>setView('list'),
                                                            children: "List"
                                                        }),
                                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                                            size: "sm",
                                                            variant: view === 'map' ? 'default' : 'outline',
                                                            "aria-pressed": view === 'map',
                                                            "data-testid": Testids.landscapes.viewMapButton,
                                                            onClick: ()=>setView('map'),
                                                            children: "Map"
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraCheckbox, {
                                                    "aria-label": "Show removed",
                                                    checked: showRemoved,
                                                    onCheckedChange: (v)=>setShowRemoved(v === true),
                                                    "data-testid": Testids.landscapes.showRemovedToggle
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    children: "Show removed"
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                members.isLoading && /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: "Loading…"
                                }),
                                members.error && /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: "Could not load members."
                                }),
                                !members.isLoading && !members.error && rows.length === 0 && /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    "data-testid": Testids.landscapes.membersEmpty,
                                    children: "No members to show."
                                }),
                                view === 'map' ? /*#__PURE__*/ _jsx(LandscapeMap, {
                                    landscapeId: id,
                                    landscapeName: l?.name ?? '',
                                    members: rows
                                }) : /*#__PURE__*/ _jsx("ul", {
                                    className: "divide-y divide-border",
                                    "aria-label": "Landscape members",
                                    children: rows.map((m)=>/*#__PURE__*/ _jsx(MemberRow, {
                                            member: m,
                                            canWrite: canWrite,
                                            removing: remove.isPending,
                                            onRemove: (opportunityId, reason)=>remove.mutate({
                                                    opportunityId,
                                                    reason
                                                }, {
                                                    onError: fail('remove member')
                                                })
                                        }, m.id || m.opportunityId))
                                }),
                                canWrite && /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                                            size: "sm",
                                            className: "flex-1",
                                            value: picked,
                                            onChange: (e)=>setPicked(e.target.value),
                                            "aria-label": "Opportunity to add",
                                            "data-testid": Testids.landscapes.addMemberSelect,
                                            children: [
                                                /*#__PURE__*/ _jsx("option", {
                                                    value: "",
                                                    children: "Add an opportunity…"
                                                }),
                                                candidates.map((o)=>/*#__PURE__*/ _jsx("option", {
                                                        value: o.id,
                                                        children: o.title
                                                    }, o.id))
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            disabled: !picked || add.isPending,
                                            "data-testid": Testids.landscapes.addMemberButton,
                                            onClick: ()=>add.mutate(picked, {
                                                    onSuccess: ()=>setPicked(''),
                                                    onError: fail('add member')
                                                }),
                                            children: "Add"
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(ProgramReferences, {
                            landscapeId: id
                        }),
                        /*#__PURE__*/ _jsx(LandscapeStory, {
                            landscapeId: id,
                            includeRemoved: showRemoved
                        }),
                        l && /*#__PURE__*/ _jsx(LandscapeWhitespace, {
                            landscapeId: id
                        }),
                        /*#__PURE__*/ _jsx(LandscapeHistory, {
                            landscapeId: id
                        })
                    ]
                })
            ]
        })
    });
}
